import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";
import { useAuth } from "@shared/hooks/useAuth";
import { Box, Button, Divider, Typography } from "@mui/material";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AuthContainer,
  ErrorMessage,
  ValidationSummaryContainer,
  ValidationSummaryItem,
  InfoTextBlock,
} from "./Auth.styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ResetPasswordFormSchema,
  RESET_PASSWORD_SCHEMA_DEFAULT_VALUES,
  resetPasswordFormSchema,
  passwordRules,
} from "./auth-form-helpers";
import { useConfirmResetPassword } from "@queries/auth/useConfirmResetPassword";
import { useMemo, useState } from "react";
import { ValidationError } from "yup";
import { PasswordTextField } from "@shared/components/PasswordTextField";
import { ConfirmDialog, SubmitButton } from "@avenue-8/sidekick-ui";

type Props = {
  email: string;
};

export const ConfirmForgotPassword = (props: Props) => {
  const { email } = props;
  const { src: logoSrc, alt: logoAlt } = useAppLogoAsset();
  const { error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEmailTroubleshootingModalOpen, setIsEmailTroubleshootingModalOpen] =
    useState(false);
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    trigger,
  } = useForm<ResetPasswordFormSchema>({
    defaultValues: {
      ...RESET_PASSWORD_SCHEMA_DEFAULT_VALUES,
    },
    resolver: yupResolver(resetPasswordFormSchema),
    reValidateMode: "onChange",
  });
  const resetPasswordMutation = useConfirmResetPassword({
    onSuccess: () => {
      reset();
    },
  });
  const loginPath = location.search ? `/login${location.search}` : "/login";

  const handleConfirmResetPassword = (data: ResetPasswordFormSchema) => {
    resetPasswordMutation.mutate({
      password: data.newPassword,
      code: data.code,
      email,
    });
  };

  const [newPassword, newPassword2] = watch(["newPassword", "newPassword2"]);
  const isPasswordDirty = !!newPassword.trim() || !!newPassword2.trim();

  const newPasswordErrors = useMemo((): Array<string> => {
    try {
      if (newPassword2?.trim()) {
        trigger("newPassword2");
      }

      resetPasswordFormSchema.validateSync(
        { newPassword, newPassword2: newPassword },
        { abortEarly: false },
      );

      return [];
    } catch (error) {
      if (error instanceof ValidationError) {
        return error.errors;
      }
      return ["Validation error"];
    }
  }, [newPassword, newPassword2, trigger]);

  if (resetPasswordMutation.isSuccess) {
    return (
      <AuthContainer>
        <header>
          <img src={logoSrc} alt={logoAlt} />
        </header>
        <Typography mt={2} mb={2} textAlign={"center"}>
          Your password has been reset successfully. You can now sign in with
          your new password.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            navigate(loginPath, { state: { email }, replace: true })
          }
        >
          Sign in
        </Button>
      </AuthContainer>
    );
  }

  const newPasswordError = Object.values(passwordRules).includes(
    errors.newPassword?.message ?? "",
  )
    ? undefined
    : errors.newPassword;

  return (
    <AuthContainer>
      <header>
        <img src={logoSrc} alt={logoAlt} />
      </header>
      <Typography mt={1} mb={1}>
        We have sent a password reset code by email. Enter it below to reset
        your password.
      </Typography>
      <Form
        onSubmit={handleSubmit(handleConfirmResetPassword)}
        aria-disabled={resetPasswordMutation.isPending}
      >
        {error && <ErrorMessage>{error.message}</ErrorMessage>}
        <TextField
          name={"code"}
          label={"Code"}
          autoComplete="code"
          inputProps={{ ...register("code"), maxLength: 6 }}
          fieldError={errors.code}
          autoFocus
        />
        <PasswordTextField
          name={"newPassword"}
          label={"New Password"}
          autoComplete="new-password"
          inputProps={{ ...register("newPassword") }}
          fieldError={newPasswordError}
        />
        <PasswordTextField
          name={"newPassword2"}
          label={"Confirm Password"}
          autoComplete="new-password"
          inputProps={{ ...register("newPassword2") }}
          fieldError={errors.newPassword2}
        />

        <ValidationSummaryContainer aria-hidden={!isPasswordDirty}>
          <Typography variant="body2" mb={0.5}>
            Password must:
          </Typography>
          {Object.entries(passwordRules).map(([key, value]) => {
            const isValid = !newPasswordErrors.includes(value);
            return (
              <ValidationSummaryItem
                variant="body2"
                color={isValid ? "success.main" : "primary"}
                key={key}
              >
                {isValid ? (
                  <CheckIcon sx={{ color: "success.main" }} fontSize="small" />
                ) : (
                  <CloseIcon sx={{ color: "primary" }} fontSize="small" />
                )}{" "}
                {value}
              </ValidationSummaryItem>
            );
          })}
        </ValidationSummaryContainer>

        <SubmitButton
          isLoading={resetPasswordMutation.isPending}
          sx={{ marginTop: isPasswordDirty ? 0 : -1 }}
        >
          Save
        </SubmitButton>

        <Box mt={1} mb={1}>
          <Divider />
        </Box>

        <Button
          onClick={() => setIsEmailTroubleshootingModalOpen(true)}
          variant="text"
          size="small"
          startIcon={<InfoIcon fontSize="small" />}
        >
          Trouble Receiving Your Email?
        </Button>
      </Form>

      <ConfirmDialog
        open={isEmailTroubleshootingModalOpen}
        dialogTitle={"Trouble Receiving Your Email?"}
        onConfirm={() => setIsEmailTroubleshootingModalOpen(false)}
        onClose={() => setIsEmailTroubleshootingModalOpen(false)}
        dialogContent={
          <div>
            <Typography mb={1}>
              If an account linked to the provided email exists and is eligible
              for a password reset, you will receive an email with further
              instructions.
            </Typography>
            <Typography mb={2}>
              Please note the following conditions under which a password reset
              may not be possible:
            </Typography>
            <InfoTextBlock>
              <strong>Single Sign-On (SSO) Accounts:</strong>
              <span>
                If your account is configured to use Single Sign-On (SSO),
                password reset through this method is not applicable. Please
                contact your organization&apos;s IT support for assistance.
              </span>
            </InfoTextBlock>
            <InfoTextBlock>
              <strong>Account Not Found:</strong>
              <span>
                If there is no account associated with the provided information,
                no email will be sent. Ensure that the email address entered is
                correct and try again.
              </span>
            </InfoTextBlock>
            <InfoTextBlock mb={2}>
              <strong>Disabled Account:</strong>
              <span>
                If your account has been disabled, you will not be able to reset
                your password. Please reach out to our support team for further
                assistance.
              </span>
            </InfoTextBlock>
            <Divider />
            <Typography variant="body2" mt={2}>
              For security reasons, we cannot disclose whether a password reset
              email was sent for any specific account. If you do not receive an
              email within a few minutes, and you believe that your account
              meets the criteria for a password reset, please check your spam
              folder or contact our support team for help.
            </Typography>
            <Typography variant="body2" mt={2}>
              <a href="mailto:support@sidekickdata.io">
                support@sidekickdata.io
              </a>
            </Typography>
          </div>
        }
        confirmLabel="Ok"
        maxWidth="sm"
      />
      <Typography textAlign={"center"} mt={1}>
        <Link to={loginPath}>Back to sign in</Link>
      </Typography>
    </AuthContainer>
  );
};
